import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Award,
  HeartHandshake,
  ImageOff,
  Loader2,
  Package,
  Ruler,
  RotateCcw,
  Search,
  Shield,
  ShoppingBag,
  Sparkles,
  Truck,
} from 'lucide-react';
import Header from '@/components/Header';
import BottomNav from '@/components/BottomNav';
import ProductCard from '@/components/ProductCard';
import CaseCard from '@/components/CaseCard';
import AnimatedBackground from '@/components/AnimatedBackground';
import PromoPopup from '@/components/PromoPopup';
import { useProducts, useCategories } from '@/hooks/useProducts';
import { useSettings } from '@/hooks/useSettings';
import { useCaseTypesWithVariants } from '@/hooks/useCaseTypes';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { getHeroImageSources } from '@/lib/imageOptimization';

const Index = () => {
  const { data: products = [], isLoading: productsLoading } = useProducts();
  const { data: categories = [], isLoading: categoriesLoading } = useCategories();
  const { data: settings } = useSettings();
  const { data: caseTypes = [] } = useCaseTypesWithVariants();
  const [selectedTab, setSelectedTab] = useState<string>('all');

  useQuery({
    queryKey: ['featured-case-id'],
    queryFn: async () => {
      const { data } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'featured_case_id')
        .maybeSingle();
      return data?.value || null;
    },
  });

  const activeCaseTypes = caseTypes.filter(
    (caseType) => caseType.is_active && caseType.variants.some((variant) => variant.stock_quantity > 0),
  );
  const showCases = selectedTab === 'cases';
  const filteredProducts =
    selectedTab === 'all' || selectedTab === 'cases'
      ? products
      : products.filter((product) => product.category === selectedTab);
  const heroImageUrl = settings?.hero_image_url;
  const heroSources = heroImageUrl ? getHeroImageSources(heroImageUrl) : null;

  const tabs = [
    { value: 'all', label: 'الكل' },
    ...(activeCaseTypes.length ? [{ value: 'cases', label: 'كفرات الآيفون' }] : []),
    ...categories
      .filter((category) => category.name !== 'كفرات')
      .map((category) => ({ value: category.name, label: category.name })),
  ];

  return (
    <div className="relative min-h-screen pb-24 md:pb-0">
      <PromoPopup />
      <AnimatedBackground />
      <Header />

      <main>
        <section className="mx-auto max-w-7xl px-3 pt-3 sm:px-6 sm:pt-6 lg:px-8">
          <div className="relative min-h-[540px] overflow-hidden rounded-[1.75rem] bg-primary shadow-lg sm:min-h-[600px] lg:min-h-[640px] lg:rounded-[2.25rem]">
            {heroSources ? (
              <picture>
                <source media="(max-width: 767px)" srcSet={heroSources.mobile} />
                <img
                  src={heroSources.desktop}
                  alt="مجموعة أصيل الجديدة"
                  className="absolute inset-0 h-full w-full object-cover"
                  width="920"
                  height="1600"
                  loading="eager"
                  decoding="async"
                  fetchPriority="high"
                />
              </picture>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-[radial-gradient(circle_at_70%_20%,hsl(35_52%_84%/0.34),transparent_34%),linear-gradient(145deg,hsl(24_25%_16%),hsl(24_30%_8%))]">
                <div className="text-center text-primary-foreground/60">
                  <ImageOff className="mx-auto mb-3 h-12 w-12" />
                  <p className="text-sm">أضف صورة الغلاف من الإعدادات</p>
                </div>
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/24 to-black/10" />
            <div className="absolute inset-x-0 bottom-0 p-6 text-white sm:p-10 lg:max-w-3xl lg:p-14">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3.5 py-2 text-xs font-semibold backdrop-blur-md sm:text-sm">
                <Sparkles className="h-4 w-4" />
                تفاصيل تعيش وياك
              </div>
              <h1 className="max-w-2xl text-4xl font-black leading-[1.12] tracking-tight sm:text-6xl lg:text-7xl">
                أصيل في كل تفصيلة
              </h1>
              <p className="mt-4 max-w-xl text-sm leading-7 text-white/[0.78] sm:text-base">
                قطع مختارة بعناية، خامات مضمونة وتجربة شراء مصممة إلك من البداية لحد باب البيت.
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/categories"
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-white px-6 font-bold text-primary shadow-xl transition hover:-translate-y-0.5 hover:bg-white/90"
                >
                  اكتشف المجموعة
                  <ArrowLeft className="h-4 w-4" />
                </Link>
                <Link
                  to="/track-order"
                  className="inline-flex min-h-12 items-center justify-center rounded-full border border-white/25 bg-white/10 px-6 font-semibold text-white backdrop-blur-md transition hover:bg-white/20"
                >
                  تتبع طلبك
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">تسوّق حسب ذوقك</p>
              <h2 className="text-2xl font-black tracking-tight sm:text-3xl">الأقسام والمنتجات</h2>
            </div>
            {!showCases && (
              <span className="w-fit rounded-full bg-secondary px-3 py-1.5 text-xs font-bold text-secondary-foreground">
                {filteredProducts.length} منتج
              </span>
            )}
          </div>

          <div className="scrollbar-hide -mx-4 mt-6 flex gap-2 overflow-x-auto px-4 pb-2 sm:mx-0 sm:px-0">
            {categoriesLoading ? (
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            ) : (
              tabs.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => setSelectedTab(tab.value)}
                  className={cn(
                    'min-h-11 shrink-0 rounded-full border px-5 text-sm font-bold transition-all',
                    selectedTab === tab.value
                      ? 'border-primary bg-primary text-primary-foreground shadow-md'
                      : 'border-border bg-card/80 text-muted-foreground hover:border-primary/30 hover:text-foreground',
                  )}
                >
                  {tab.label}
                </button>
              ))
            )}
          </div>

          {showCases ? (
            <div className="mt-8">
              <div className="mb-5 flex items-center justify-between">
                <h3 className="text-lg font-black">كفرات الآيفون</h3>
                <Link to="/cases" className="flex items-center gap-1 text-sm font-bold text-muted-foreground hover:text-primary">
                  عرض الكل <ArrowLeft className="h-4 w-4" />
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-5 lg:grid-cols-4">
                {activeCaseTypes.map((caseType) => (
                  <CaseCard key={caseType.id} caseType={caseType} />
                ))}
              </div>
            </div>
          ) : productsLoading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="mt-8 rounded-[1.5rem] border border-dashed border-border bg-card/70 px-6 py-16 text-center">
              <ShoppingBag className="mx-auto mb-4 h-9 w-9 text-muted-foreground" />
              <h3 className="font-bold">القسم قيد التجهيز</h3>
              <p className="mt-1 text-sm text-muted-foreground">المنتجات الجديدة راح تنضاف هنا قريبًا</p>
            </div>
          ) : (
            <div className="mt-8 grid grid-cols-2 gap-x-3 gap-y-8 sm:grid-cols-3 sm:gap-x-5 lg:grid-cols-4 lg:gap-x-6 lg:gap-y-10">
              {filteredProducts.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>
          )}
        </section>

        <section className="border-y border-border/70 bg-card/60">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 md:grid-cols-[0.8fr_1.2fr] lg:px-8 lg:py-20">
            <div className="self-center">
              <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">ليش أصيل؟</p>
              <h2 className="text-3xl font-black leading-tight tracking-tight sm:text-4xl">الجودة مو إضافة، هي الأساس</h2>
              <p className="mt-4 max-w-md text-sm leading-7 text-muted-foreground">
                نهتم بالتفاصيل الصغيرة حتى توصلك قطعة تفرح بيها، من الخامة والتقنية إلى التغليف والتوصيل.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              {[
                { icon: Package, title: 'تغليف مميز', description: 'تغليف مرتب يليق بهديتك' },
                { icon: Award, title: 'تقنيات حديثة', description: 'تفاصيل ذكية بتقنية NFC' },
                { icon: HeartHandshake, title: 'خامة مضمونة', description: 'جودة تعيش وياك أكثر' },
              ].map((item) => (
                <article key={item.title} className="rounded-[1.5rem] border border-border/70 bg-card p-5 shadow-sm">
                  <div className="mb-8 flex h-11 w-11 items-center justify-center rounded-2xl bg-accent text-accent-foreground">
                    <item.icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-black">{item.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">{item.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
          <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">من الاختيار إلى باب البيت</p>
              <h2 className="text-3xl font-black tracking-tight sm:text-4xl">طلبك بخطوات واضحة</h2>
            </div>
            <Link to="/track-order" className="flex w-fit items-center gap-2 text-sm font-bold text-muted-foreground transition hover:text-foreground">
              تتبع طلب موجود <ArrowLeft className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid gap-3 md:grid-cols-3 md:gap-5">
            {[
              { number: '01', icon: Search, title: 'اختار القطعة', text: 'تصفح الأقسام وحدد المقاس واللون والتفاصيل المناسبة إلك.' },
              { number: '02', icon: ShoppingBag, title: 'راجع سلتك', text: 'شوف المنتجات والكميات والتوصيل قبل ما تثبت الطلب.' },
              { number: '03', icon: Truck, title: 'تابع الطلب', text: 'استخدم رقم الطلب أو الهاتف حتى تعرف آخر تحديث للتوصيل.' },
            ].map((step) => (
              <article key={step.number} className="relative overflow-hidden rounded-[1.6rem] border border-border bg-card p-6 shadow-sm">
                <span className="absolute left-5 top-4 text-4xl font-black text-muted/80">{step.number}</span>
                <div className="mb-10 flex h-11 w-11 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
                  <step.icon className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-black">{step.title}</h3>
                <p className="mt-2 text-sm leading-7 text-muted-foreground">{step.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 pb-14 sm:px-6 lg:px-8 lg:pb-20">
          <div className="grid overflow-hidden rounded-[1.75rem] border border-border bg-card shadow-sm md:grid-cols-[1.1fr_0.9fr]">
            <div className="p-6 sm:p-8 lg:p-10">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">كل المعلومات بمكان واحد</p>
              <h2 className="mt-3 text-2xl font-black sm:text-3xl">قبل الطلب وبعده، التفاصيل واضحة</h2>
              <p className="mt-3 max-w-xl text-sm leading-7 text-muted-foreground">دليل المقاسات وسياسة الاسترجاع والبحث السريع موجودة حتى تختار بثقة وتوصل للمعلومة بدون تعقيد.</p>
            </div>
            <div className="grid grid-cols-3 border-t border-border bg-muted/55 md:border-r md:border-t-0">
              {[
                { to: '/size-guide', icon: Ruler, label: 'دليل المقاسات' },
                { to: '/return-policy', icon: RotateCcw, label: 'الاسترجاع' },
                { to: '/search', icon: Search, label: 'بحث سريع' },
              ].map((item) => (
                <Link key={item.to} to={item.to} className="flex min-h-32 flex-col items-center justify-center gap-3 border-l border-border px-2 text-center last:border-l-0 transition hover:bg-card">
                  <item.icon className="h-5 w-5" />
                  <span className="text-xs font-black sm:text-sm">{item.label}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 divide-x divide-x-reverse divide-border rounded-[1.5rem] border border-border bg-card/80 p-3 shadow-sm sm:p-5">
            {[
              { icon: Truck, label: 'شحن سريع' },
              { icon: Shield, label: 'جودة مضمونة' },
              { icon: Sparkles, label: 'اختيارات أصلية' },
            ].map((feature) => (
              <div key={feature.label} className="flex flex-col items-center justify-center gap-2 px-2 text-center sm:flex-row">
                <feature.icon className="h-5 w-5" />
                <span className="text-[11px] font-bold sm:text-sm">{feature.label}</span>
              </div>
            ))}
          </div>
        </section>

        <footer className="border-t border-border bg-primary text-primary-foreground">
          <div className="mx-auto flex max-w-7xl flex-col gap-6 px-5 py-10 text-center sm:px-6 md:flex-row md:items-center md:justify-between md:text-right lg:px-8">
            <div>
              <p className="text-xl font-black">أصيل</p>
              <p className="mt-1 text-xs text-primary-foreground/60">جميع الحقوق محفوظة © {new Date().getFullYear()}</p>
            </div>
            <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm font-semibold text-primary-foreground/72 md:justify-end">
              <Link to="/track-order" className="hover:text-white">تتبع طلبك</Link>
              <Link to="/size-guide" className="hover:text-white">دليل المقاسات</Link>
              <Link to="/return-policy" className="hover:text-white">سياسة الاسترجاع</Link>
            </div>
          </div>
        </footer>
      </main>

      <BottomNav />
    </div>
  );
};

export default Index;
